import moment from 'moment';
import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Link, Stack, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { openTaskViewer } from 'src/store/slices/app/appSlice';
import { User } from 'src/services/organisation/organisation.types';
import { ITask, TaskType, TaskStatus } from 'src/services/tasks/tasks.types';
import { useMarkTaskCompletedMutation } from 'src/services/tasks/tasks.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

const DATE_FORMAT = 'DD MMM YYYY, hh:mm A';

const TYPE_ICONS: Record<TaskType, string> = {
  [TaskType.GENERAL]: 'mdi:clipboard-text-outline',
  [TaskType.TO_DO]: 'mdi:checkbox-marked-outline',
  [TaskType.CALL]: 'solar:phone-bold',
  [TaskType.EMAIL]: 'mdi:email-outline',
};

const fullName = (account?: Partial<User> | null) =>
  `${account?.first_name ?? ''} ${account?.last_name ?? ''}`.trim();

type Props = {
  task: ITask;
};

/**
 * One task in a profile's Tasks block (JT1-1796): type, title, description, due date, assignee and
 * who created it, then either Mark complete or who completed it and when.
 */
export default function LinkedTaskItem({ task }: Props) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [markTaskCompleted, { isLoading: isCompleting }] = useMarkTaskCompletedMutation();

  const isCompleted = task.status === TaskStatus.COMPLETED;
  const isOverdue = !isCompleted && !!task.due_at && moment(task.due_at).isBefore(moment());

  const handleMarkComplete = useCallback(async () => {
    try {
      await markTaskCompleted({ taskId: task.id }).unwrap();
      enqueueSnackbar(t('tasks.api.complete.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('tasks.api.default_error'), { variant: 'error' });
    }
  }, [markTaskCompleted, task.id, enqueueSnackbar, t]);

  const assignee = fullName(task.assigned_to);
  const completedBy = fullName(task.completed_by);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: isCompleted ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Label
          variant="soft"
          color={isCompleted ? 'default' : 'info'}
          startIcon={<Iconify icon={TYPE_ICONS[task.type] ?? TYPE_ICONS[TaskType.GENERAL]} />}
        >
          {t(`tasks.editor-sidesheet.form.type.options.${task.type}`)}
        </Label>

        <Link
          component="button"
          type="button"
          variant="subtitle2"
          color="text.primary"
          underline="hover"
          onClick={() => dispatch(openTaskViewer({ taskId: task.id }))}
          sx={{ textAlign: 'left', wordBreak: 'break-word' }}
        >
          {task.title}
        </Link>
      </Stack>

      {task.description && (
        <TruncatedTextField
          text={task.description}
          mode="lines"
          limit={3}
          showMoreEnabled
          variant="body2"
          color="text.secondary"
          style={{ whiteSpace: 'pre-wrap' }}
        />
      )}

      <Stack direction="row" alignItems="center" columnGap={2} rowGap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: isOverdue ? 'error.main' : 'text.secondary' }}>
          <Iconify icon="mdi:clock-outline" width={14} />
          <Typography variant="caption" color="inherit">
            {task.due_at
              ? t('tasks.linked.due', { date: moment(task.due_at).format(DATE_FORMAT) })
              : t('tasks.linked.no_due_date')}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
          <Iconify icon="hugeicons:user" width={14} />
          <Typography variant="caption" color="inherit">
            {assignee ? t('tasks.linked.assigned_to', { name: assignee }) : t('tasks.linked.unassigned')}
          </Typography>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.disabled" component="p" sx={{ mt: 0.5 }}>
        {t('tasks.linked.created_by', {
          name: fullName(task.created_by),
          date: moment(task.created_at).format(DATE_FORMAT),
        })}
      </Typography>

      {isCompleted ? (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: 'success.main' }}>
          <Iconify icon="mdi:check-circle" width={16} />
          <Typography variant="caption" color="inherit" fontWeight="fontWeightMedium">
            {completedBy
              ? t('tasks.linked.completed_by', { name: completedBy, date: moment(task.completed_at).format(DATE_FORMAT) })
              : t('tasks.linked.completed_at', { date: moment(task.completed_at).format(DATE_FORMAT) })}
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <LoadingButton
            size="small"
            variant="outlined"
            color="success"
            loading={isCompleting}
            onClick={handleMarkComplete}
            startIcon={<Iconify icon="mdi:check-circle-outline" />}
          >
            {t('tasks.linked.mark_complete')}
          </LoadingButton>
        </Stack>
      )}
    </Box>
  );
}
